<template>
  <div class="thumb-up-wrap ht-night-exclude-node" @click.stop="togglePraise">
    <span v-if="thumbupCount > 0" :class="thumbUpCountClass" class="thumb-up-count">{{
      thumbupCount ? thumbupCount : ''
    }}</span>
    <span v-else class="thumb-up-count thumb-up-text">赞</span>

    <i :class="praiseIconClass">
      <component :is="praiseIconName" v-show="!switchedLottie" class="thumb-up-svg" />
      <span ref="praiseIcon" class="thumb-up-icon-wrap" />
    </i>
  </div>
</template>

<script lang="ts">
import { computed, Ref, ref, toRefs, watch, defineComponent } from 'vue';
import lottie from 'lottie-web';
// import type { AnimationItem } from 'lottie-web';
import wv, { showToast } from '@cpc/comment-sdk-jsapi';
import { scriptOnReady } from '@cpc/lib-js-require';
import {
  usePraiseButton,
  useGlobalState,
  useCommentListInject,
} from '@cpc/comment-sdk-core-comment';
import { constant } from '@cpc/comment-sdk-common';

import like from '../assets/lottie/comment-like.json';
import likeDark from '../assets/lottie/comment-like-dark.json';
import { contentTypeMap } from '../assets/js/constant';
import { commonCommentStat } from '../core/useStat';
// 不适用css引入svg，改用 component是为了解决无图模式需要显示图片问题
import { PROD_LOTTIE_SDK_JS_LINK } from '../constants';

import Praised from './PraiseIcon/Praised.vue';
import PraisedDark from './PraiseIcon/PraisedDark.vue';
import Praise from './PraiseIcon/Praise.vue';
import PraiseDark from './PraiseIcon/PraiseDark.vue';

export default defineComponent({
  name: 'PraiseButton',

  components: {
    Praised,
    PraisedDark,
    Praise,
    PraiseDark,
  },

  props: {
    commentId: {
      type: String,
      required: true,
      default: '',
    },

    replyId: {
      type: String,
      required: false,
      default: '',
    },

    isThumbUp: {
      type: Boolean,
      default: true,
    },

    thumbupCount: {
      type: Number,
      default: 0,
    },

    replyContent: {
      type: String,
      required: false,
      default: '',
    },

    commentOwner: {
      type: Object,
      default() {
        return {};
      },
    },

    replyOwner: {
      type: Object,
      default() {
        return {};
      },
    },
  },

  setup(props) {
    const { LoadStatus } = constant;

    const { commentId, replyId, isThumbUp, thumbupCount, replyOwner, commentOwner } = toRefs(props);
    const { getNewCommentContentById, getTopCommentContentById } = useCommentListInject();

    const replyContent = props.replyContent;
    const ownerId = replyOwner.value.id || commentOwner.value.id || '';

    const { togglePraiseThrottle, loadStatus: praiseLoadStatus } = usePraiseButton({
      commentId,
      replyId,
      isThumbUp,
      thumbupCount,
      replyContent,
      ownerId,
    });

    const { isNightMode, documentMeta, pageType } = toRefs(useGlobalState());

    const praiseIcon: Ref<Element | null> = ref(null);

    // false：使用svg，true： 使用lottie
    const switchedLottie = ref(false);
    const lottiePlaying = ref(false);

    let animation: any;

    const praiseIconClass = computed(() => {
      return {
        'thumb-up-icon': true,
        'lottie-playing': lottiePlaying.value,
      };
    });

    const praiseIconName = computed(() => {
      return isThumbUp.value
        ? isNightMode.value
          ? 'PraisedDark'
          : 'Praised'
        : isNightMode.value
          ? 'PraiseDark'
          : 'Praise';
    });

    const thumbUpCountClass = computed(() => {
      return isThumbUp.value
        ? isNightMode.value
          ? 'is-thumb-up-count-dark'
          : 'is-thumb-up-count'
        : '';
    });

    watch(isThumbUp, () => {
      animation?.destroy();

      if (praiseIcon.value && lottie) {
        handlePraise();
      } else {
        scriptOnReady({
          url: window.pageData?.lottieSdkUrl || PROD_LOTTIE_SDK_JS_LINK,
          namespace: 'lottie',
          callback: () => {
            handlePraise();
          },
        });
      }
    });

    const handlePraise = () => {
      animation?.destroy();

      animation = (lottie).loadAnimation({
        container: praiseIcon.value,
        renderer: 'svg',
        loop: false,
        autoplay: false,
        animationData: isNightMode.value ? likeDark : like,
      });

      if (isThumbUp.value) {
        animation.playSegments([0, 39], true);
      } else {
        animation.playSegments([39, 55], true);
      }

      animation?.addEventListener('complete', () => {
        lottiePlaying.value = false;
      });

      switchedLottie.value = true;
      animation?.play();
      lottiePlaying.value = true;
    };

    const togglePraise = () => {
      if (praiseLoadStatus.value != LoadStatus.DEFAULT) {
        return;
      }

      const praiseThumbupCount = ref(0);

      togglePraiseThrottle(
        () => {
          if (isThumbUp.value) {
            praiseThumbupCount.value = thumbupCount.value - 1;
          } else {
            praiseThumbupCount.value = thumbupCount.value + 1;
          }

          commonCommentStat(isThumbUp.value ? 20084702 : 20084703, {
            isReply: !!replyId.value,
            strId: replyId.value ? replyId.value : commentId.value,
            commentId: commentId.value,
            commentContent:
              getNewCommentContentById(commentId.value)
              || getTopCommentContentById(commentId.value),
            replyId: replyId.value,
            replyContent: replyId.value ? replyContent : '',
            thumbupCount: praiseThumbupCount.value,

            beThumbedId: ownerId,
          });

          if (isThumbUp.value) {
            // 点赞埋点
            wv.asyncInvoke('statLikeClickEvent');

            const { fromId, mediaName, source, url, title, styleType = '1' } = documentMeta.value;

            // TODO(libiao): 参数补齐
            wv.asyncInvoke(
              'generalStat',
              20084011,
              21006,
              10012,
              JSON.stringify({
                fromId,
                sourceMedia: mediaName,
                source,
                url,
                title,
                category: contentTypeMap[styleType],
                commentPageType: pageType.value === 2 ? 'secondComment' : 'topComment',
              }),
            );
          } else {
            // 取消点赞埋点
            wv.asyncInvoke('statdisLikeClickEvent');
          }
        },
        (error) => {
          console.warn(error);

          if (error.code === 1401 || error.code === 1403) {
            // showToast(error.message);
          } else if (error.code === 500 || error.code === 502 || error.code === 504) {
            showToast('服务器异常，请稍后重试');
          } else if (error.code === 10002) {
            showToast(error.message);
          } else if (error.code === 30001) {
            showToast('网络信号差，请稍后重试');
          } else {
            showToast('操作频繁，请稍后再试');
          }
        },
      );
    };

    return {
      praiseIcon,
      praiseLoadStatus,
      togglePraise,
      switchedLottie,
      praiseIconClass,
      praiseIconName,
      thumbUpCountClass,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '../assets/css/styling-mixin.scss';

.thumb-up-wrap {
  display: flex;
  // align-items: flex-start;
  padding: 0 24px;
  margin-right: -24px;
  border-radius: 36px;
  &:active {
    background-color: rgba(0, 0, 0, 0.06);
  }
  @include os-browser-styling('', '', false, false, true) {
    &:active {
      background-color: rgba(240, 240, 240, 1);
    }
  }
  @include os-browser-styling('', '', false, false, true, true) {
    &:active {
      background-color: rgba($color: #fff, $alpha: 0.15);
    }
  }
}

.thumb-up-count {
  // height: 48px;
  line-height: 48px;
  font-size: 36px;
  font-weight: 400;
  margin: 13.5px 0 10.5px;
  white-space: nowrap;

  display: flex;
  align-items: center;
  color: rgba($color: #000, $alpha: 0.54);
  @include os-browser-styling('', '', false, false, true, true) {
    color: rgba($color: #fff, $alpha: 0.54);
  }
}

.is-thumb-up-count {
  color: #db382c;
}

.is-thumb-up-count-dark {
  color: #ff6c61;
}

.thumb-up-icon {
  position: relative;
  width: 48px;
  // height: 48px;
  background-size: 100% 100%;
  // overflow: hidden;
  margin: 12px 0 15px 9px;
  color: rgba($color: #000, $alpha: 0.54);
}

.thumb-up-svg {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
}

.thumb-up-icon-wrap {
  width: 48px;
  height: 48px;
  position: absolute;

  svg {
    position: absolute;
    top: 0;
  }
}

.lottie-playing {
  overflow: visible !important;
}
</style>
